import React from "react";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useContentValue } from "../../ContentContext";

export const Location = () => {
    const hours = useContentValue("hours", { mon_thu: "11 AM – 7 PM", fri_sat: "11 AM – 8 PM", sun: "11 AM – 5 PM" });
    const loc = useContentValue("location", {
        address_line1: "280 First St,",
        address_line2: "Collingwood, ON",
        address_line3: "L9Y 1B1",
        address_note: "Located Behind Treetz Cannabis",
        phone: "+1 (705) 888-0943",
        phone_href: "tel:+17058880943",
        email: "strakesandwichco@gmail.com",
        instagram: "https://instagram.com/strakesandwichco",
        facebook: "https://facebook.com/strakesandwichco",
        map_query: "280 First St, Collingwood, ON L9Y 1B1",
    });

    return (
        <section
            data-testid="location-section"
            className="bg-[#1A1A1A] text-[#F5F2EB] py-12 px-6"
        >
            <div className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#E8A87C] font-semibold mb-3 font-body">
                    — Come Find Us
                </div>
                <h2 className="font-display text-[40px] sm:text-5xl uppercase font-bold leading-[1] tracking-tight whitespace-nowrap">
                    Visit <span className="italic font-normal text-[#E8A87C]">Strake.</span>
                </h2>
            </div>

            {/* Map */}
            <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] mb-8 border border-white/10 overflow-hidden">
                <iframe
                    title="Strake Sandwich Co Location"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(loc.map_query || "280 First St, Collingwood, ON L9Y 1B1")}&output=embed`}
                    className="absolute inset-0 w-full h-full grayscale-[40%] contrast-110"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="location-map"
                />
            </div>

            {/* Address + Hours */}
            <div className="grid grid-cols-1 gap-7">
                <div data-testid="location-address">
                    <div className="flex items-start gap-3 mb-2">
                        <MapPin size={18} strokeWidth={1.8} className="text-[#E8A87C] mt-1 flex-shrink-0" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8A87C] font-semibold mb-1 font-body">
                                Address
                            </div>
                            <div className="font-display text-xl sm:text-2xl uppercase font-medium leading-tight tracking-tight">
                                {loc.address_line1}
                                <br />
                                {loc.address_line2}
                                <br />
                                {loc.address_line3}
                            </div>
                            <div className="mt-2 text-sm text-white/70 font-body italic">
                                {loc.address_note}
                            </div>

                            {/* Inline hours */}
                            <div
                                className="mt-5 pt-4 border-t border-white/10 space-y-1"
                                data-testid="location-hours"
                            >
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8A87C] font-semibold mb-2 font-body">
                                    Hours
                                </div>
                                <div className="font-display text-sm sm:text-base uppercase tracking-[0.05em] text-[#F5F2EB]/90">
                                    Mon – Thu <span className="text-[#E8A87C] mx-1">·</span> {hours.mon_thu}
                                </div>
                                <div className="font-display text-sm sm:text-base uppercase tracking-[0.05em] text-[#F5F2EB]/90">
                                    Fri – Sat <span className="text-[#E8A87C] mx-1">·</span> {hours.fri_sat}
                                </div>
                                <div className="font-display text-sm sm:text-base uppercase tracking-[0.05em] text-[#F5F2EB]/90">
                                    Sun <span className="text-[#E8A87C] mx-1">·</span> {hours.sun}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact links */}
                <div className="pt-6 border-t border-white/10 space-y-5">
                    <a
                        data-testid="contact-phone-link"
                        href={loc.phone_href || `tel:${(loc.phone || "").replace(/[^0-9+]/g, "")}`}
                        className="flex items-center gap-3 group"
                    >
                        <Phone size={18} strokeWidth={1.8} className="text-[#E8A87C] flex-shrink-0" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8A87C] font-semibold mb-0.5 font-body">
                                Call
                            </div>
                            <div className="font-display text-xl sm:text-2xl uppercase font-bold tracking-tight border-b-2 border-[#F5F2EB] pb-1 inline-block group-hover:border-[#E8A87C] group-hover:text-[#E8A87C] transition-colors">
                                {loc.phone}
                            </div>
                        </div>
                    </a>

                    <a
                        data-testid="contact-email-link"
                        href={`mailto:${loc.email}`}
                        className="flex items-center gap-3 group"
                    >
                        <Mail size={18} strokeWidth={1.8} className="text-[#E8A87C] flex-shrink-0" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8A87C] font-semibold mb-0.5 font-body">
                                Email
                            </div>
                            <div className="font-display text-base sm:text-lg uppercase font-bold tracking-tight border-b-2 border-[#F5F2EB] pb-1 inline-block group-hover:border-[#E8A87C] group-hover:text-[#E8A87C] transition-colors break-all">
                                {loc.email}
                            </div>
                        </div>
                    </a>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
                <div className="font-display text-2xl sm:text-3xl uppercase font-bold tracking-tight">
                    Strake
                </div>
                <div className="flex items-center gap-4">
                    <a
                        data-testid="footer-instagram-link"
                        href={loc.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="text-[#F5F2EB]/75 hover:text-[#E8A87C] transition-colors"
                    >
                        <Instagram size={20} strokeWidth={1.6} />
                    </a>
                    <a
                        data-testid="footer-facebook-link"
                        href={loc.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="text-[#F5F2EB]/75 hover:text-[#E8A87C] transition-colors"
                    >
                        <Facebook size={20} strokeWidth={1.6} />
                    </a>
                </div>
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/30 font-body">
                © 2026 Strake Sandwich Co.
            </div>
        </section>
    );
};
